import React, { useState, useEffect } from 'react';
import { Row, Col, List, Card, Typography, Tag, Avatar, Empty, Spin, Button, App, Popconfirm } from 'antd';
import { MailOutlined, UserOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import emailService from '../../services/emailService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const EmailHistory = ({ leadId, isDeal = false, refreshTrigger }) => {
  const { message } = App.useApp();
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (leadId) {
      fetchEmails();
    }
  }, [leadId, refreshTrigger, isDeal]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      let result;
      if (isDeal) {
        // For deals, fetch emails via the deal endpoint
        result = await emailService.getDealEmails(leadId);
      } else {
        // For leads, use the existing lead endpoint
        result = await emailService.getLeadEmails(leadId);
      }
      if (result.success) {
        setEmails(result.data.emails || []);
        // Auto-select first email if available
        if (result.data.emails && result.data.emails.length > 0 && !selectedEmail) {
          setSelectedEmail(result.data.emails[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
      message.error('Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (emailId) => {
    setDeleting(emailId);
    try {
      const result = await emailService.deleteEmail(emailId);
      if (result.success) {
        message.success('Email deleted successfully');
        // Remove from list
        setEmails(emails.filter(e => e._id !== emailId));
        // Clear selection if deleted email was selected
        if (selectedEmail?._id === emailId) {
          setSelectedEmail(null);
        }
      }
    } catch (error) {
      console.error('Error deleting email:', error);
      message.error('Failed to delete email');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'scheduled':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <MailOutlined />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'scheduled':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No emails sent yet"
      />
    );
  }

  return (
    <Row gutter={16} style={{ height: '600px' }}>
      {/* Column 1: Email List */}
      <Col span={10}>
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>
                <MailOutlined style={{ marginRight: 8 }} />
                Sent Emails ({emails.length})
              </span>
            </div>
          }
          styles={{ body: { padding: 0, height: 'calc(600px - 57px)', overflowY: 'auto' } }}
        >
          <List
            dataSource={emails}
            renderItem={(email) => (
              <List.Item
                key={email._id}
                onClick={() => setSelectedEmail(email)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectedEmail?._id === email._id ? '#f0f5ff' : 'transparent',
                  padding: '12px 16px',
                  borderLeft: selectedEmail?._id === email._id ? '3px solid #1890ff' : '3px solid transparent'
                }}
                className="email-list-item"
              >
                <List.Item.Meta
                  avatar={
                    <Avatar icon={getStatusIcon(email.status)} />
                  }
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong ellipsis style={{ maxWidth: '70%' }}>
                        {email.subject}
                      </Text>
                      <Tag color={getStatusColor(email.status)} style={{ marginLeft: 8 }}>
                        {email.status?.toUpperCase()}
                      </Tag>
                    </div>
                  }
                  description={
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        To: {email.to}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {dayjs(email.sentAt).format('MMM DD, YYYY HH:mm')}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      </Col>

      {/* Column 2: Email Preview */}
      <Col span={14}>
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Email Preview</span>
              {selectedEmail && (
                <Popconfirm
                  title="Delete this email?"
                  description="This action cannot be undone."
                  onConfirm={() => handleDelete(selectedEmail._id)}
                  okText="Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleting === selectedEmail._id}
                  >
                    Delete
                  </Button>
                </Popconfirm>
              )}
            </div>
          }
          styles={{ body: { height: 'calc(600px - 57px)', overflowY: 'auto' } }}
        >
          {selectedEmail ? (
            <div className="email-preview">
              {/* Email Header */}
              <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                <Title level={4} style={{ marginBottom: 12 }}>
                  {selectedEmail.subject}
                </Title>
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>From: </Text>
                  <Text>{selectedEmail.from}</Text>
                </div>
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>To: </Text>
                  <Text>{selectedEmail.to}</Text>
                </div>
                
                {selectedEmail.cc && selectedEmail.cc.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>CC: </Text>
                    <Text>{selectedEmail.cc.join(', ')}</Text>
                  </div>
                )}
                
                {selectedEmail.bcc && selectedEmail.bcc.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>BCC: </Text>
                    <Text>{selectedEmail.bcc.join(', ')}</Text>
                  </div>
                )}
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Date: </Text>
                  <Text>{dayjs(selectedEmail.sentAt).format('MMMM DD, YYYY [at] HH:mm')}</Text>
                </div>
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Status: </Text>
                  <Tag color={getStatusColor(selectedEmail.status)} icon={getStatusIcon(selectedEmail.status)}>
                    {selectedEmail.status?.toUpperCase()}
                  </Tag>
                </div>
                
                {selectedEmail.sentBy && (
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Sent By: </Text>
                    <Text>
                      {selectedEmail.sentBy.firstName} {selectedEmail.sentBy.lastName} ({selectedEmail.sentBy.email})
                    </Text>
                  </div>
                )}
                
                {selectedEmail.status === 'failed' && selectedEmail.errorMessage && (
                  <div style={{ marginTop: 12 }}>
                    <Tag color="error" style={{ padding: '4px 8px' }}>
                      Error: {selectedEmail.errorMessage}
                    </Tag>
                  </div>
                )}
              </div>

              {/* Email Body */}
              <div style={{ marginTop: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>Message:</Title>
                {selectedEmail.htmlBody ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: selectedEmail.htmlBody }}
                    style={{
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  />
                ) : (
                  <Paragraph
                    style={{
                      whiteSpace: 'pre-wrap',
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    {selectedEmail.body}
                  </Paragraph>
                )}
              </div>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Select an email to view details"
            />
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default EmailHistory;
